/**
 * Type definitions for table lineage visualization.
 * Matches backend Pydantic models after camelCase transformation.
 */

export const ServiceType = {
  SNOWFLAKE: "snowflake",
  S3: "s3",
} as const;

export type ServiceType = (typeof ServiceType)[keyof typeof ServiceType];

export const LineageNodeType = {
  TABLE: "table",
  COLUMN: "column",
  CONTAINER: "container",
} as const;

export type LineageNodeType = (typeof LineageNodeType)[keyof typeof LineageNodeType];

interface BaseLineageNode {
  id: string;
  name: string;
  fullyQualifiedName: string;
  service?: string;
  type?: string;
}

export interface TableLineageNode extends BaseLineageNode {
  type: "table";
  databaseName: string;
  schemaName: string;
}

export interface ColumnLineageNode extends BaseLineageNode {
  type: "column";
  databaseName: string;
  schemaName: string;
  table: string;
}

export interface ContainerLineageNode extends BaseLineageNode {
  type: "container";
  bucket: string;
  filePath?: string;
}

export interface GenericLineageNode extends BaseLineageNode {
  type?: string;
}

export type LineageNode =
  | TableLineageNode
  | ColumnLineageNode
  | ContainerLineageNode
  | GenericLineageNode;

export interface LineageEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
}

export interface TableLineageResponse {
  nodes: LineageNode[];
  edges: LineageEdge[];
}

export const NodeDirection = {
  UPSTREAM: "upstream",
  DOWNSTREAM: "downstream",
  CURRENT: "current",
} as const;

export type NodeDirection = (typeof NodeDirection)[keyof typeof NodeDirection];

export type EnhancedNodeData = LineageNode & {
  direction: NodeDirection;
  isCurrentTable: boolean;
};

/**
 * Type guards for lineage nodes.
 */
export const isTableNode = (node: LineageNode): node is TableLineageNode =>
  node.type === LineageNodeType.TABLE;

export const isColumnNode = (node: LineageNode): node is ColumnLineageNode =>
  node.type === LineageNodeType.COLUMN;

export const isContainerNode = (node: LineageNode): node is ContainerLineageNode =>
  node.type === LineageNodeType.CONTAINER;

export const isGenericNode = (node: LineageNode): node is GenericLineageNode =>
  !isTableNode(node) && !isColumnNode(node) && !isContainerNode(node);
